import { AionLogoArtwork } from "@/components/aion/AionLogoArtwork";
import { AionHolographicFace } from "@/components/aion/AionHolographicFace";
import { getVoiceStateCopy, normalizeVoiceState, VOICE_STATES } from "@/constants/voiceRuntime";

const bars = [0.42, 0.78, 1.08, 0.78, 0.42];
const fallbackIntensity = {
  [VOICE_STATES.IDLE]: 0.12,
  [VOICE_STATES.WAKE]: 0.38,
  [VOICE_STATES.LISTENING]: 0.44,
  [VOICE_STATES.SPEECH_DETECTED]: 0.68,
  [VOICE_STATES.TRANSCRIBING]: 0.48,
  [VOICE_STATES.THINKING]: 0.36,
  [VOICE_STATES.EXECUTING]: 0.54,
  [VOICE_STATES.RESPONDING]: 0.46,
  [VOICE_STATES.SPEAKING]: 0.52,
  [VOICE_STATES.INTERRUPTED]: 0.2,
  [VOICE_STATES.MUTED]: 0.1,
  [VOICE_STATES.ERROR]: 0.24,
};

const STATE_EXPRESSIONS = {
  [VOICE_STATES.IDLE]: "calm",
  [VOICE_STATES.WAKE]: "attentive",
  [VOICE_STATES.LISTENING]: "attentive",
  [VOICE_STATES.SPEECH_DETECTED]: "attentive",
  [VOICE_STATES.TRANSCRIBING]: "focused",
  [VOICE_STATES.THINKING]: "focused",
  [VOICE_STATES.EXECUTING]: "focused",
  [VOICE_STATES.RESPONDING]: "warm",
  [VOICE_STATES.SPEAKING]: "warm",
  [VOICE_STATES.INTERRUPTED]: "calm",
  [VOICE_STATES.MUTED]: "calm",
  [VOICE_STATES.ERROR]: "concerned",
};

const ALLOWED_EXPRESSIONS = new Set(["calm", "attentive", "focused", "warm", "stern", "surprised", "concerned"]);

export const AionVisualPresence = ({
  audioOutputLevel = 0,
  audioInputLevel = 0,
  avatarProfile = "female",
  expression,
  state = VOICE_STATES.IDLE,
  statusLabel,
  subtitle,
  title,
  wakeWord = "AION",
}) => {
  const normalizedState = normalizeVoiceState(state);
  const copy = getVoiceStateCopy(normalizedState, { wakeWord });
  const speakingState = [VOICE_STATES.RESPONDING, VOICE_STATES.SPEAKING, VOICE_STATES.EXECUTING].includes(normalizedState);
  const safeInputLevel = Number.isFinite(audioInputLevel) ? Math.max(0, Math.min(1, audioInputLevel)) : 0;
  const safeOutputLevel = Number.isFinite(audioOutputLevel) ? Math.max(0, Math.min(1, audioOutputLevel)) : 0;
  const realLevel = speakingState ? safeOutputLevel : safeInputLevel;
  const reactiveLevel = Number.isFinite(realLevel) && realLevel > 0 ? realLevel : 0;
  const intensity = Math.max(fallbackIntensity[normalizedState] || 0.12, Math.min(1, reactiveLevel));
  const energyLevel = Math.max(0.12, intensity);
  const mouthLevel = normalizedState === VOICE_STATES.SPEAKING
    ? Math.max(0.22, safeOutputLevel)
    : 0;
  const stateLabel = statusLabel || normalizedState.replaceAll("_", " ");
  const showMutedBadge = normalizedState === VOICE_STATES.MUTED;
  const showErrorBadge = normalizedState === VOICE_STATES.ERROR;
  const resolvedAvatarProfile = avatarProfile === "male" ? "male" : "female";
  const requestedExpression = typeof expression === "string" ? expression.toLowerCase() : "";
  const stateExpression = STATE_EXPRESSIONS[normalizedState] || "calm";
  const resolvedExpression = normalizedState === VOICE_STATES.IDLE && ALLOWED_EXPRESSIONS.has(requestedExpression)
    ? requestedExpression
    : stateExpression;

  return (
    <section
      className={`aion-visual-presence aion-visual-presence-${normalizedState.toLowerCase()} aion-avatar-profile-${resolvedAvatarProfile} aion-avatar-expression-${resolvedExpression}`}
      style={{
        "--aion-presence-level": intensity.toFixed(3),
        "--aion-energy-level": energyLevel.toFixed(3),
        "--aion-input-level": safeInputLevel.toFixed(3),
        "--aion-output-level": safeOutputLevel.toFixed(3),
        "--aion-mouth-level": mouthLevel.toFixed(3),
      }}
      data-avatar-profile={resolvedAvatarProfile}
      data-expression={resolvedExpression}
      data-testid="aion-visual-presence"
    >
      <div className="aion-visual-presence-stage" data-testid="aion-visual-presence-stage">
        <span className="aion-visual-depth-plane" aria-hidden="true" />
        <span className="aion-visual-glow" aria-hidden="true" />
        <span className="aion-visual-halo" aria-hidden="true" />
        <span className="aion-visual-ring aion-visual-ring-outer" aria-hidden="true" />
        <span className="aion-visual-ring aion-visual-ring-middle" aria-hidden="true" />
        <span className="aion-visual-ring aion-visual-ring-inner" aria-hidden="true" />
        <span className="aion-visual-wave aion-visual-wave-input" aria-hidden="true" />
        <span className="aion-visual-wave aion-visual-wave-output" aria-hidden="true" />
        <span className="aion-visual-signal aion-visual-signal-left" aria-hidden="true" />
        <span className="aion-visual-signal aion-visual-signal-right" aria-hidden="true" />

        <div className="aion-visual-orbit" aria-hidden="true">
          <span className="aion-visual-orbit-dot aion-visual-orbit-dot-1" />
          <span className="aion-visual-orbit-dot aion-visual-orbit-dot-2" />
          <span className="aion-visual-orbit-dot aion-visual-orbit-dot-3" />
        </div>

        <div className="aion-avatar aion-avatar-holographic" data-testid="aion-avatar" aria-label={`AION Avatar ${resolvedAvatarProfile}, Ausdruck ${resolvedExpression}`}>
          <AionHolographicFace profile={resolvedAvatarProfile} expression={resolvedExpression} />
        </div>

        <div className="aion-visual-core aion-visual-core-avatar" data-testid="aion-visual-presence-core">
          <AionLogoArtwork
            variant="compact"
            className="aion-visual-logo-shell"
            imageClassName="aion-visual-logo"
            dataTestId="aion-visual-logo"
          />
        </div>

        <div className="aion-visual-bars" aria-hidden="true" data-testid="aion-visual-presence-bars">
          {bars.map((multiplier, index) => (
            <span
              key={multiplier + index}
              className="aion-visual-bar"
              style={{ transform: `scaleY(${Math.max(0.18, intensity * multiplier + 0.22)})` }}
            />
          ))}
        </div>

        {showMutedBadge ? <span className="aion-visual-badge aion-visual-badge-muted" data-testid="aion-visual-muted-badge">Mic muted</span> : null}
        {showErrorBadge ? <span className="aion-visual-badge aion-visual-badge-error" data-testid="aion-visual-error-badge">Connection issue</span> : null}
      </div>

      <div className="aion-visual-presence-copy" data-testid="aion-visual-presence-copy">
        <p className="aion-section-eyebrow" data-testid="aion-visual-presence-status">{stateLabel}</p>
        <h3 className="aion-visual-presence-title" data-testid="aion-visual-presence-title">{title || copy.title}</h3>
        <p className="aion-visual-presence-subtitle" data-testid="aion-visual-presence-subtitle">{subtitle || copy.subtitle}</p>
      </div>
    </section>
  );
};

